import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import useDebounce from "@/hooks/useDebounce";
import { useGetTransactionsApiCallQuery } from "@/services/api/transactions/transactions.api";
import { CheckCircle, Clock, ExternalLink } from "lucide-react";
import React, { useState } from "react";
import Loader from "@/components/Loader";

const MoreClaims = () => {
  const [searchTerm, setSearchTerm] = useState("");

  const debounceSearchTerm = useDebounce(searchTerm, 500);

  const {
    data: transactionsData,
    isError,
    isLoading,
  } = useGetTransactionsApiCallQuery({
    search: debounceSearchTerm || "",
    status: "",
  });

  const getStatusInfo = (status: string) => {
    switch (status) {
      case "completed":
        return {
          icon: CheckCircle,
          text: "Completed",
          color: "text-green-400",
          bgColor: "bg-green-500/20",
        };
      case "processing":
        return {
          icon: Clock,
          text: "Processing",
          color: "text-yellow-400",
          bgColor: "bg-yellow-500/20",
        };
      case "pending":
        return {
          icon: Clock,
          text: "Pending",
          color: "text-gray-400",
          bgColor: "bg-gray-500/20",
        };
      default:
        return {
          icon: Clock,
          text: "Unknown",
          color: "text-gray-400",
          bgColor: "bg-gray-500/20",
        };
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader size="lg" text="Loading claims..." />
      </div>
    );
  }

  return (
    <div>
      {/* Claims List */}
      <div className="space-y-4">
        {!isError &&
          transactionsData?.result?.docs &&
          transactionsData?.result?.docs?.length > 0 &&
          transactionsData?.result?.docs.map((claim) => {
            const StatusIcon = getStatusInfo(claim.status).icon;
            const statusInfo = getStatusInfo(claim.status);

            return (
              <Card
                key={claim._id}
                className="bg-gray-900/50 border-yellow-500/20 hover:border-yellow-500/40 transition-colors"
              >
                <CardContent className="p-4 md:p-6">
                  <div className="flex flex-col md:flex-row md:items-center justify-between space-y-4 md:space-y-0">
                    <div className="flex items-start space-x-4">
                      <div
                        className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${statusInfo.bgColor}`}
                      >
                        <StatusIcon className={`w-5 h-5 ${statusInfo.color}`} />
                      </div>

                      <div className="min-w-0 flex-1">
                        <h3 className="text-white font-semibold mb-1">
                          {claim.campaignId?.name}
                        </h3>
                        <div className="flex items-center space-x-4 text-sm text-gray-400">
                          <span>
                            {new Date(claim.createdAt).toLocaleDateString()}
                          </span>
                          {claim.createdAt && (
                            <span>
                              {new Date(claim.createdAt).toLocaleTimeString()}
                            </span>
                          )}
                        </div>
                        {claim.hash && (
                          <div className="flex items-center space-x-2 mt-2">
                            <span className="text-xs text-gray-500">TX:</span>
                            <code className="text-xs text-gray-400 bg-gray-800 px-2 py-1 rounded">
                              {/* {claim.txHash.slice(0, 10)}...
                                  {claim.txHash.slice(-8)} */}
                              {claim.hash}
                            </code>
                            <Button
                              size="sm"
                              variant="ghost"
                              className="text-gray-400 hover:text-white p-1 h-auto"
                              onClick={() =>
                                navigator.clipboard.writeText(claim.hash!)
                              }
                            >
                              <ExternalLink className="w-3 h-3" />
                            </Button>
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="flex items-center space-x-4">
                      <div className="text-right">
                        <div className="text-yellow-400 font-semibold text-lg">
                          {claim.amount} BINGOLD
                        </div>
                        <Badge
                          className={`${statusInfo.bgColor} ${statusInfo.color} border-0`}
                        >
                          {statusInfo.text}
                        </Badge>
                      </div>

                      {claim.hash && (
                        <Button
                          size="sm"
                          variant="outline"
                          className="border-gray-600 text-gray-300 hover:bg-gray-700"
                          onClick={() =>
                            window.open(
                              `https://bscscan.com/tx/${claim.hash}`,
                              "_blank",
                            )
                          }
                        >
                          <ExternalLink className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}

        {(isError || transactionsData?.result?.docs?.length === 0) && (
          <div className="text-center">
            <p className="text-gray-400">No claims found.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default MoreClaims;
